import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, CheckCircle2, XCircle, BookOpen, ShieldAlert, ShoppingCart, BookMarked, Leaf, ArrowRight, Clock, Battery, Brain, Flame, X as XIcon } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const CtaButton = ({
  children,
  href,
  onClick,
  className = ''
}: {
  children: React.ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
}) => {
  const handleClick = () => {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      document.getElementById('final-cta')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
    if (onClick) onClick();
  };
  return <motion.button whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={handleClick} className={`bg-gold hover:bg-gold-dark text-emerald-900 font-bold py-4 px-8 rounded-md shadow-lg transition-colors flex items-center justify-center gap-2 text-lg sm:text-xl w-full sm:w-auto ${className}`} data-id="element-2">
      {children}
      <ArrowRight className="w-5 h-5" data-id="element-3" />
    </motion.button>;
};
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeModal, setActiveModal] = useState<'terminos' | 'privacidad' | null>(null);
  useEffect(() => {
    if (!(window as any).fbq) {
      const n = ((window as any).fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      }) as any;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [] as any[];
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }
    const fbq = (window as any).fbq;
    fbq('init', '962279486187488');
    fbq('track', 'PageView');
  }, []);
  const faqs = [{
    q: '¿Pasaré hambre?',
    a: 'No. El plan incluye comidas densas en nutrientes que te mantienen satisfecho.'
  }, {
    q: '¿Es difícil de seguir?',
    a: 'Está diseñado para personas ocupadas; no necesitas horas en la cocina.'
  }, {
    q: '¿Tengo que comprar suplementos caros?',
    a: 'No, todo lo que necesitas está en un supermercado normal.'
  }, {
    q: '¿Qué pasa después de los 10 días?',
    a: 'Sabrás exactamente cómo manejar el azúcar de forma social sin volver a ser su esclavo.'
  }];
  return <div className="min-h-screen" data-id="element-4">
      {/* HERO SECTION */}
      <section className="bg-emerald-900 text-cream pt-20 pb-24 px-4 relative overflow-hidden" data-id="element-5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-6">
          <FadeIn data-id="element-7">
            <div className="inline-block border border-gold/30 text-gold px-4 py-1.5 rounded-full text-sm font-medium tracking-wide mb-6" data-id="element-8">
              EL CÓDIGO DETOX
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-9">
              Rompe tu Dependencia al Azúcar en Solo 10 Días
            </h1>
            <h2 className="text-xl sm:text-2xl text-cream-dark/90 font-sans font-light leading-relaxed mb-8" data-id="element-10">
              Recupera tu energía, desinflama tu cuerpo y apaga los antojos
              incontrolables sin usar fuerza de voluntad.
            </h2>
            <div className="space-y-6 mb-10 text-cream/80 text-lg leading-relaxed" data-id="element-11">
              <p data-id="element-12">
                Si tienes más de 40 años, sabes que la lucha contra el azúcar no
                es una cuestión de "falta de ganas". Es una batalla biológica.
                Terminas de comer y tu cuerpo te exige algo dulce. Llegan las 4
                de la tarde y tu energía se desploma, obligándote a buscar un
                dopaje de cafeína o carbohidratos solo para terminar el día.
              </p>
              <p data-id="element-13">
                No es que seas débil. Es que tu metabolismo ha sido secuestrado
                por un ciclo de insulina que la fuerza de voluntad no puede
                romper por sí sola.
              </p>
            </div>
            <CtaButton data-id="element-14">¡QUIERO LIBERARME DEL AZÚCAR!</CtaButton>
          </FadeIn>

          <FadeIn delay={0.2} className="relative lg:h-[600px] flex items-center justify-center" data-id="element-15">
            <div className="absolute inset-0 bg-gold/10 rounded-full blur-3xl" data-id="element-16"></div>
            <img src="https://cdn.magicpatterns.com/uploads/6cksv21E9r9YsK4p2Cnx1f/portada.png" alt="Código Detox eBook en Tablet y Celular" className="relative z-10 max-w-full h-auto object-contain drop-shadow-2xl" data-id="element-17" />
          </FadeIn>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-24 px-4 bg-cream" data-id="element-18">
        <div className="max-w-3xl mx-auto text-center" data-id="element-19">
          <FadeIn data-id="element-20">
            <h2 className="text-3xl sm:text-4xl font-bold text-emerald-900 mb-8" data-id="element-21">
              El Problema Invisible Después de los 40
            </h2>
            <div className="text-lg text-emerald-800/80 space-y-6 text-left" data-id="element-22">
              <p data-id="element-23">
                Después de los 40, el cuerpo procesa la glucosa de manera
                diferente. Lo que antes era un gusto, ahora se convierte en
                grasa abdominal persistente, neblina mental y una inflamación
                silenciosa que te hace sentir cansado desde que te despiertas.
              </p>
              <p data-id="element-24">
                Has intentado dejarlo antes, pero el dolor de cabeza, la
                irritabilidad y la ansiedad te hacen volver a caer. La culpa te
                invade, piensas que "no tienes disciplina" y el ciclo vuelve a
                empezar, dañando tu salud y tu confianza cada día más.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-24 px-4 bg-cream-dark" data-id="element-25">
        <div className="max-w-5xl mx-auto" data-id="element-26">
          <FadeIn data-id="element-27">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-emerald-900 mb-16" data-id="element-28">
              Por qué lo que has intentado no ha funcionado
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 gap-8" data-id="element-29">
            {[{
            icon: <Brain className="w-8 h-8 text-gold" data-id="element-30" />,
            text: 'Las dietas de "cortar todo de golpe" fallan porque no preparan la bioquímica de tu cerebro para la abstinencia.'
          }, {
            icon: <Flame className="w-8 h-8 text-gold" data-id="element-31" />,
            text: 'Los productos "light" o con edulcorantes artificiales mantienen vivo el deseo cerebral por el dulce, asegurando que vuelvas a caer.'
          }, {
            icon: <Battery className="w-8 h-8 text-gold" data-id="element-32" />,
            text: 'Contar calorías no sirve de nada si tus hormonas del hambre están disparadas por los picos de azúcar.'
          }, {
            icon: <Clock className="w-8 h-8 text-gold" data-id="element-33" />,
            text: 'Intentar usar solo la fuerza de voluntad es una batalla perdida contra millones de años de evolución biológica.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-34">
                <div className="bg-cream p-8 rounded-xl border border-emerald-900/10 h-full flex gap-6" data-id="element-35">
                  <div className="flex-shrink-0 mt-1" data-id="element-36">{item.icon}</div>
                  <p className="text-emerald-900/80 text-lg leading-relaxed" data-id="element-37">
                    {item.text}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-4 bg-emerald-900 text-cream" data-id="element-38">
        <div className="max-w-4xl mx-auto text-center" data-id="element-39">
          <FadeIn data-id="element-40">
            <h2 className="text-3xl sm:text-4xl font-bold text-gold mb-8" data-id="element-41">
              El Método de Sustitución Bioquímica
            </h2>
            <p className="text-xl mb-8 font-light" data-id="element-42">
              La solución no es "comer menos".
            </p>
            <div className="text-lg text-cream/90 space-y-6 text-left bg-emerald-800/50 p-8 sm:p-12 rounded-2xl border border-gold/20" data-id="element-43">
              <p data-id="element-44">
                Este método no se enfoca en lo que te quitas, sino en lo que le
                das a tu cuerpo para "apagar" la señal de auxilio del cerebro.
              </p>
              <p data-id="element-45">
                Al inundar tus receptores con micronutrientes específicos y
                grasas inteligentes, el hambre por azúcar simplemente
                desaparece. No tienes que resistir el antojo, porque el antojo
                deja de existir.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 px-4 bg-cream" data-id="element-46">
        <div className="max-w-5xl mx-auto" data-id="element-47">
          <FadeIn data-id="element-48">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-emerald-900 mb-16" data-id="element-49">
              Lo que obtienes hoy
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-50">
            {[{
            icon: <BookOpen className="w-6 h-6" data-id="element-51" />,
            title: 'El eBook Código Detox',
            desc: 'La guía paso a paso para limpiar tu sistema en 10 días.'
          }, {
            icon: <ShieldAlert className="w-6 h-6" data-id="element-52" />,
            title: 'Protocolo de Emergencia',
            desc: 'Qué hacer cuando el antojo ataca (la regla de los 15 minutos).'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-53">
                <div className="bg-white p-8 rounded-xl border border-emerald-900/10 shadow-sm h-full hover:shadow-md transition-shadow" data-id="element-54">
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center text-emerald-900 mb-6" data-id="element-55">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900 mb-3" data-id="element-56">
                    {item.title}
                  </h3>
                  <p className="text-emerald-800/70" data-id="element-57">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR / NOT FOR */}
      <section className="py-24 px-4 bg-cream-dark" data-id="element-58">
        <div className="max-w-5xl mx-auto" data-id="element-59">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-60">
            <FadeIn data-id="element-61">
              <div className="bg-white p-8 sm:p-10 rounded-2xl border-t-4 border-emerald-700 shadow-sm h-full" data-id="element-62">
                <h3 className="text-2xl font-bold text-emerald-900 mb-8 flex items-center gap-3" data-id="element-63">
                  <CheckCircle2 className="w-8 h-8 text-emerald-700" data-id="element-64" />
                  Esto ES para ti si:
                </h3>
                <ul className="space-y-6" data-id="element-65">
                  {['Hombres y mujeres de 40+ que sienten que el azúcar controla sus tardes.', 'Personas que sufren de inflamación abdominal y fatiga crónica.', 'Quienes están cansados de dietas restrictivas que solo generan ansiedad.', 'Emprendedores y profesionales que necesitan claridad mental y energía estable para trabajar.'].map((text, i) => <li key={i} className="flex gap-4 text-emerald-800/80" data-id="element-66">
                      <CheckCircle2 className="w-6 h-6 text-emerald-700 flex-shrink-0" data-id="element-67" />
                      <span data-id="element-68">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-69">
              <div className="bg-white p-8 sm:p-10 rounded-2xl border-t-4 border-red-700 shadow-sm h-full" data-id="element-70">
                <h3 className="text-2xl font-bold text-emerald-900 mb-8 flex items-center gap-3" data-id="element-71">
                  <XCircle className="w-8 h-8 text-red-700" data-id="element-72" />
                  Esto NO es para ti si:
                </h3>
                <ul className="space-y-6" data-id="element-73">
                  {['Personas que buscan una "pastilla mágica" sin cambiar un solo hábito alimenticio.', 'Quienes no están dispuestos a seguir un plan estructurado por solo 10 días.', 'Personas con condiciones médicas graves que no consultarán a su médico antes de un cambio nutricional.'].map((text, i) => <li key={i} className="flex gap-4 text-emerald-800/80" data-id="element-74">
                      <XCircle className="w-6 h-6 text-red-700 flex-shrink-0" data-id="element-75" />
                      <span data-id="element-76">{text}</span>
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 px-4 bg-cream" data-id="element-77">
        <div className="max-w-5xl mx-auto" data-id="element-78">
          <FadeIn data-id="element-79">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-emerald-900 mb-16" data-id="element-80">
              Cómo funciona el proceso de 10 días
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-81">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gold/30 -translate-y-1/2 z-0" data-id="element-82"></div>
            {[{
            phase: 'Fase de Limpieza',
            days: 'Días 1-3',
            desc: 'Inundamos tu cuerpo con minerales clave para detener la urgencia biológica.'
          }, {
            phase: 'Fase de Estabilización',
            days: 'Días 4-7',
            desc: 'Tu páncreas descansa y tu energía se vuelve lineal y constante.'
          }, {
            phase: 'Fase de Libertad',
            days: 'Días 8-10',
            desc: 'Tus papilas gustativas se resetean; el control ahora es totalmente tuyo.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.2} className="relative z-10" data-id="element-83">
                <div className="bg-white p-8 rounded-xl border border-emerald-900/10 shadow-sm text-center h-full" data-id="element-84">
                  <div className="w-16 h-16 bg-emerald-900 text-gold rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6 shadow-lg" data-id="element-85">
                    {i + 1}
                  </div>
                  <div className="text-sm font-bold text-gold tracking-wider uppercase mb-2" data-id="element-86">
                    {item.days}
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900 mb-4" data-id="element-87">
                    {item.phase}
                  </h3>
                  <p className="text-emerald-800/70" data-id="element-88">{item.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-24 px-4 bg-emerald-900 text-cream relative overflow-hidden" data-id="element-89">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(197,165,90,0.15)_0%,transparent_70%)]" data-id="element-90"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-91">
          <FadeIn data-id="element-92">
            <div className="text-gold mb-8" data-id="element-93">
              <svg className="w-12 h-12 mx-auto opacity-50" fill="currentColor" viewBox="0 0 24 24" data-id="element-94">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" data-id="element-95" />
              </svg>
            </div>
            <p className="text-2xl sm:text-3xl font-serif leading-relaxed mb-8" data-id="element-96">
              "En los primeros 7 días, notarás cómo la 'neblina mental'
              desaparece y tus niveles de energía por la tarde se mantienen
              altos sin necesidad de snacks. Es el momento en que tu ropa
              empieza a sentirse más holgada, no por perder músculo, sino por
              liberar la retención de líquidos causada por la inflamación."
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 bg-cream" data-id="element-97">
        <div className="max-w-3xl mx-auto" data-id="element-98">
          <FadeIn data-id="element-99">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-emerald-900 mb-16" data-id="element-100">
              Preguntas Frecuentes
            </h2>
          </FadeIn>
          <div className="space-y-4" data-id="element-101">
            {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-102">
                <div className="border border-emerald-900/10 rounded-lg bg-white overflow-hidden" data-id="element-103">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left px-6 py-5 flex items-center justify-between focus:outline-none" data-id="element-104">
                    <span className="font-bold text-lg text-emerald-900 pr-8" data-id="element-105">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gold transition-transform duration-300 flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-106" />
                  </button>
                  <motion.div initial={false} animate={{
                height: openFaq === i ? 'auto' : 0,
                opacity: openFaq === i ? 1 : 0
              }} className="overflow-hidden" data-id="element-107">
                    <div className="px-6 pb-5 text-emerald-800/70" data-id="element-108">{faq.a}</div>
                  </motion.div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK */}
      <section id="offer-section" className="py-24 px-4 bg-cream-dark" data-id="element-109">
        <div className="max-w-4xl mx-auto" data-id="element-110">
          <FadeIn data-id="element-111">
            <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-gold/20 text-center relative overflow-hidden" data-id="element-112">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-gold-light via-gold to-gold-dark" data-id="element-113"></div>

              <h2 className="text-3xl sm:text-4xl font-bold text-emerald-900 mb-6" data-id="element-114">
                Recupera el control de tu salud hoy mismo
              </h2>
              <p className="text-xl text-emerald-800/80 mb-10 max-w-2xl mx-auto" data-id="element-115">
                Accede al sistema completo "Código Detox" por un pago único. Es
                menos de lo que gastas en cafés y pan dulce en un día.
              </p>

              <div className="bg-cream-dark inline-block px-8 py-6 rounded-2xl mb-10 border border-emerald-900/5" data-id="element-116">
                <div className="text-sm font-bold text-emerald-900/60 uppercase tracking-wider mb-2" data-id="element-117">
                  Precio Especial de Lanzamiento
                </div>
                <div className="text-5xl sm:text-6xl font-bold text-emerald-900 mb-2" data-id="element-118">
                  $6.39{' '}
                  <span className="text-2xl text-emerald-900/60" data-id="element-119">USD</span>
                </div>
              </div>

              <div id="final-cta" className="max-w-md mx-auto" data-id="element-120">
                <CtaButton href="https://www.legadosalud.com/cart/42425306513493:1" onClick={() => (window as any).fbq?.('track', 'AddToCart')} className="w-full py-5 text-xl mb-6" data-id="element-121">
                  ¡QUIERO LIBERARME DEL AZÚCAR!
                </CtaButton>
                <p className="text-sm text-emerald-800/60 flex items-center justify-center gap-2" data-id="element-122">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700" data-id="element-123" />
                  Acceso instantáneo después de tu compra.
                </p>
                <p className="text-sm text-emerald-800/60 mt-2" data-id="element-124">
                  Formato digital compatible con celular, tablet y PC.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-emerald-900 text-cream/60 py-12 px-4 text-center text-sm" data-id="element-125">
        <div className="max-w-4xl mx-auto" data-id="element-126">
          <p className="mb-4" data-id="element-127">
            © {new Date().getFullYear()} Código Detox. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center justify-center gap-6 mb-4" data-id="element-128">
            <button onClick={() => setActiveModal('terminos')} className="underline hover:text-cream transition-colors" data-id="element-129">
              Términos y Condiciones
            </button>
            <span className="text-cream/30" data-id="element-130">|</span>
            <button onClick={() => setActiveModal('privacidad')} className="underline hover:text-cream transition-colors" data-id="element-131">
              Aviso de Privacidad
            </button>
          </div>
          <p className="max-w-2xl mx-auto text-xs opacity-70" data-id="element-132">
            Este producto no sustituye el consejo médico profesional. Consulta a
            tu médico antes de comenzar cualquier programa de nutrición,
            especialmente si tienes condiciones médicas preexistentes.
          </p>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-emerald-900/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-133">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2
      }} className="bg-cream rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()} data-id="element-134">
            <div className="sticky top-0 bg-cream border-b border-emerald-900/10 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-135">
              <h3 className="text-xl font-bold text-emerald-900 font-serif" data-id="element-136">
                {activeModal === 'terminos' ? 'Términos y Condiciones' : 'Aviso de Privacidad'}
              </h3>
              <button onClick={() => setActiveModal(null)} className="text-emerald-900/60 hover:text-emerald-900 transition-colors" data-id="element-137">
                <XIcon className="w-6 h-6" data-id="element-138" />
              </button>
            </div>
            <div className="px-6 py-8 text-emerald-800/80 space-y-4 text-sm leading-relaxed" data-id="element-139">
              {activeModal === 'terminos' ? <>
                  <p className="font-bold text-emerald-900" data-id="element-140">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                  </p>
                  <p data-id="element-141">
                    Al adquirir el producto digital "Código Detox", aceptas los
                    siguientes términos y condiciones de uso:
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-142">
                    1. Producto Digital
                  </h4>
                  <p data-id="element-143">
                    El "Código Detox" es un producto digital informativo en
                    formato eBook. Una vez realizada la compra, recibirás acceso
                    instantáneo al material. Debido a la naturaleza digital del
                    producto, no se aceptan devoluciones ni reembolsos una vez
                    entregado el acceso.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-144">
                    2. Uso Personal
                  </h4>
                  <p data-id="element-145">
                    El contenido adquirido es para uso personal y exclusivo del
                    comprador. Queda estrictamente prohibida la reproducción,
                    distribución, reventa o compartición del material sin
                    autorización expresa por escrito.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-146">
                    3. Descargo de Responsabilidad Médica
                  </h4>
                  <p data-id="element-147">
                    El contenido de este eBook es únicamente informativo y
                    educativo. No sustituye el consejo, diagnóstico o
                    tratamiento médico profesional. Consulta a tu médico antes
                    de realizar cualquier cambio en tu alimentación,
                    especialmente si padeces alguna condición médica
                    preexistente.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-148">
                    4. Resultados
                  </h4>
                  <p data-id="element-149">
                    Los resultados pueden variar de persona a persona. No se
                    garantizan resultados específicos, ya que dependen de
                    factores individuales como el estado de salud, la adherencia
                    al programa y el estilo de vida.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-150">
                    5. Propiedad Intelectual
                  </h4>
                  <p data-id="element-151">
                    Todo el contenido del "Código Detox" está protegido por
                    derechos de autor. Cualquier uso no autorizado será
                    perseguido conforme a la legislación aplicable.
                  </p>
                </> : <>
                  <p className="font-bold text-emerald-900" data-id="element-152">
                    Última actualización:{' '}
                    {new Date().toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
                  </p>
                  <p data-id="element-153">
                    Tu privacidad es importante para nosotros. Este aviso
                    describe cómo recopilamos, usamos y protegemos tu
                    información personal.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-154">
                    1. Información que Recopilamos
                  </h4>
                  <p data-id="element-155">
                    Al realizar tu compra, recopilamos únicamente la información
                    necesaria para procesar tu pedido: nombre, dirección de
                    correo electrónico y datos de pago. Los datos de pago son
                    procesados de forma segura por nuestro proveedor de pagos y
                    no son almacenados en nuestros servidores.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-156">
                    2. Uso de la Información
                  </h4>
                  <p data-id="element-157">
                    Tu información personal se utiliza exclusivamente para:
                    entregar el producto digital adquirido, enviarte
                    confirmación de compra y, si lo autorizas, enviarte
                    comunicaciones relacionadas con contenido de salud y
                    bienestar.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-158">
                    3. Protección de Datos
                  </h4>
                  <p data-id="element-159">
                    Implementamos medidas de seguridad técnicas y organizativas
                    para proteger tu información personal contra acceso no
                    autorizado, alteración, divulgación o destrucción.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-160">
                    4. No Compartimos tu Información
                  </h4>
                  <p data-id="element-161">
                    No vendemos, alquilamos ni compartimos tu información
                    personal con terceros, excepto cuando sea necesario para
                    procesar tu compra o cuando la ley lo requiera.
                  </p>
                  <h4 className="font-bold text-emerald-900 mt-6" data-id="element-162">
                    5. Tus Derechos
                  </h4>
                  <p data-id="element-163">
                    Tienes derecho a acceder, rectificar, cancelar u oponerte al
                    tratamiento de tus datos personales en cualquier momento,
                    conforme a la Ley Federal de Protección de Datos Personales
                    en Posesión de los Particulares (LFPDPPP).
                  </p>
                </>}
            </div>
          </motion.div>
        </div>}
    </div>;
}